import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Audit log — Phase 2.10 viewer.
 *
 * The server exposes four endpoints used by this module:
 *   GET  /api/audit-log                  — filtered, keyset-paginated list
 *   GET  /api/audit-log/export           — CSV of the same filter set
 *   GET  /api/audit-log/reviews/latest   — last review's timestamp (anchors "Since last review" preset)
 *   POST /api/audit-log/reviews          — logs a §164.308(a)(1)(ii)(D) review
 *
 * The list endpoint returns a plain array in the body and `X-Next-Cursor` in
 * a response header (array shape is preserved for legacy client + existing
 * call sites that `res.body.find(...)` the response).
 *
 * Note: no §164.528 disclosure-accounting export lives here. The audit log
 * only captures internal TPO access — which §164.528(a)(1) excludes from
 * the accounting entirely — so exporting it would mislead the requester.
 * If the app later grows a disclosure-log module for external releases
 * (subpoenas, public-health, required-by-law), the export belongs there.
 */

export type AuditResult = 'Success' | 'Failure' | 'Other';

export interface AuditLogEntry {
    id: string;
    ts: string;
    actor: string;
    userId: string | null;
    category: string;
    action: string;
    subject: string;
    result: string;
    chainOrd: string | null;
}

export interface AuditLogFilter {
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    category?: string;
    action?: string;
    subject?: string;
    result?: AuditResult;
}

export interface AuditLogPage {
    entries: AuditLogEntry[];
    nextCursor: string | null;
    /** Total matching rows across all pages. Capped at 1000; see `totalCapped`. */
    total: number;
    /** True when the server hit the count cap — UI should render "1000+" etc. */
    totalCapped: boolean;
}

async function fetchAuditLog(
    filter: AuditLogFilter,
    limit: number,
    before: string | null,
): Promise<AuditLogPage> {
    const params: Record<string, string | number> = { limit };
    if (filter.dateFrom) params.dateFrom = filter.dateFrom;
    if (filter.dateTo)   params.dateTo   = filter.dateTo;
    if (filter.userId)   params.userId   = filter.userId;
    if (filter.category) params.category = filter.category;
    if (filter.action)   params.action   = filter.action;
    if (filter.subject)  params.subject  = filter.subject;
    if (filter.result)   params.result   = filter.result;
    if (before)          params.before   = before;

    const res = await api.get<AuditLogEntry[]>('/audit-log', { params });
    // Server puts the keyset cursor in a response header so the body stays a
    // plain array for legacy consumers. An empty string means "no more pages".
    const raw = (res.headers['x-next-cursor'] ?? '') as string;
    const nextCursor = raw ? raw : null;
    const totalRaw = (res.headers['x-total-count'] ?? '0') as string;
    const total = Number.parseInt(totalRaw, 10) || 0;
    const totalCapped = ((res.headers['x-total-count-capped'] ?? '0') as string) === '1';
    return { entries: res.data, nextCursor, total, totalCapped };
}

export function useAuditLog(filter: AuditLogFilter, limit = 100, before: string | null = null) {
    return useQuery({
        queryKey: [
            'audit-log',
            filter.dateFrom ?? '',
            filter.dateTo ?? '',
            filter.userId ?? '',
            filter.category ?? '',
            filter.action ?? '',
            filter.subject ?? '',
            filter.result ?? '',
            limit,
            before ?? '',
        ] as const,
        queryFn: () => fetchAuditLog(filter, limit, before),
        // Audit-log reviews are forensic — freshness matters. Disable stale
        // caching across filter changes to avoid showing a stale page while
        // a reviewer is adjusting the scope.
        staleTime: 0,
    });
}

// ── CSV export (filtered list) ───────────────────────────────────────────────

function triggerDownload(blob: Blob, filename: string) {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
}

function parseFilename(cd: string | undefined, fallback: string): string {
    if (!cd) return fallback;
    const match = /filename="([^"]+)"/.exec(cd);
    return match ? match[1] : fallback;
}

export function useExportAuditLog() {
    return useMutation({
        mutationFn: async (filter: AuditLogFilter): Promise<{ filename: string }> => {
            const params: Record<string, string> = {};
            if (filter.dateFrom) params.dateFrom = filter.dateFrom;
            if (filter.dateTo)   params.dateTo   = filter.dateTo;
            if (filter.userId)   params.userId   = filter.userId;
            if (filter.category) params.category = filter.category;
            if (filter.action)   params.action   = filter.action;
            if (filter.subject)  params.subject  = filter.subject;
            if (filter.result)   params.result   = filter.result;

            const res = await api.get('/audit-log/export', {
                params,
                responseType: 'blob',
            });
            const filename = parseFilename(
                res.headers['content-disposition'] as string | undefined,
                'audit-log.csv',
            );
            triggerDownload(res.data as Blob, filename);
            return { filename };
        },
    });
}

// ── Mark-reviewed (§164.308(a)(1)(ii)(D)) ────────────────────────────────────

export interface AuditReviewPayload {
    notes?: string;
}

export interface LatestAuditReview {
    id: string;
    reviewedAt: string;
}

/**
 * Timestamp of the most recent audit-log review (or null if none yet).
 * Anchors the "Since last review" filter preset on AuditLogPage.
 */
export function useLatestAuditReview() {
    return useQuery({
        queryKey: ['audit-log', 'reviews', 'latest'] as const,
        queryFn: async (): Promise<LatestAuditReview | null> => {
            const res = await api.get('/audit-log/reviews/latest', {
                validateStatus: (s) => s === 200 || s === 204,
            });
            if (res.status === 204) return null;
            return res.data as LatestAuditReview;
        },
        staleTime: 0,
    });
}

export function useCreateAuditReview() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: AuditReviewPayload) => {
            const res = await api.post('/audit-log/reviews', payload);
            return res.data;
        },
        onSuccess: () => {
            // A review writes an "Audit log reviewed" row into the same log
            // we're viewing, so refresh every audit-log query. Also bumps
            // the "Since last review" anchor for future reviews.
            qc.invalidateQueries({ queryKey: ['audit-log'] });
        },
    });
}
